import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Plus } from 'lucide-react';
import { TICKET_PRIORITIES, TICKET_STATUSES, TICKET_TYPES } from '@mkcore/support/schema';
import { Button, Checkbox, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton } from '@mkcore/ui';
import { DataTable, type ColumnDef } from '@/components/data-table';
import { formatDateTime } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { NewTicketDialog, TicketPriorityBadge, TicketStatusBadge, ticketsQuery, type Ticket } from '@/features/support/tickets';
import { organisationsQuery } from '@/features/crm/queries';
import { engagementsQuery } from '@/features/engagements/queries';
import { SlaBadge } from '@/features/support/sla';
import { ItemAgentBadge, useItemAgentStatus } from '@/features/agents/item-status';
import { SupportOverview } from '@/features/overviews';

const search = z.object({
  // The router parses search values as JSON: a search for 12 arrives as a number.
  q: z
    .union([z.string(), z.number()])
    .optional()
    .transform((v) => (v === undefined ? undefined : String(v))),
  status: z.enum([...TICKET_STATUSES, 'all']).optional(),
  priority: z.enum(TICKET_PRIORITIES).optional(),
  mine: z.boolean().optional(),
  unassigned: z.boolean().optional(),
  sla: z.enum(['at_risk', 'breached', 'paused']).optional(),
  organisationId: z.string().uuid().optional(),
  engagementId: z.string().uuid().optional(),
  sort: z.enum(['sla', 'updated', 'created', 'priority', 'number']).optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/support/')({ validateSearch: search, component: SupportPage });

/** The fixed views of the list (CO-0613); views of your own are saved per person in the table toolbar. */
const VIEWS: { key: string; search: Partial<Search> }[] = [
  { key: 'mineOpen', search: { mine: true } },
  { key: 'unassigned', search: { unassigned: true } },
  { key: 'slaSoon', search: { sla: 'at_risk' } },
];
const viewOf = (s: Search) => VIEWS.find((v) => JSON.stringify(Object.fromEntries(Object.entries(s).filter(([, x]) => x !== undefined))) === JSON.stringify(v.search))?.key;

/** The ticket list (CO-0613): open tickets first, with filters, columns to pick and saved views. */
function SupportPage() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const message = useErrorMessage();
  const canCreate = useCan('support:create');
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const orgs = useInfiniteQuery(organisationsQuery({ limit: 200 }));
  const engagements = useInfiniteQuery({ ...engagementsQuery({ organisationId: s.organisationId, limit: 100 }), enabled: !!s.organisationId });
  const view = viewOf(s);
  const q = useInfiniteQuery(
    ticketsQuery({
      q: s.q || undefined,
      status: s.status === 'all' ? [...TICKET_STATUSES] : s.status ? [s.status] : undefined,
      priority: s.priority ? [s.priority] : undefined,
      mine: s.mine || undefined,
      unassigned: s.unassigned || undefined,
      slaState: s.sla === 'at_risk' ? ['at_risk', 'breached'] : s.sla ? [s.sla] : undefined,
      organisationId: s.organisationId,
      engagementId: s.engagementId,
      sort: s.sort,
      limit: 100,
    }),
  );
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const agentStatus = useItemAgentStatus('ticket', rows.map((r) => r.id));
  const columns = useMemo<ColumnDef<Ticket>[]>(
    () => [
      {
        id: 'subject',
        label: t('tickets.fields.subject'),
        value: (r) => `${r.key} ${r.subject}`,
        cell: (r) => (
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-fg-3 tabular">{r.key}</span>
            <Link to="/support/$id" params={{ id: r.id }} className="font-bold hover:underline">
              {r.subject}
            </Link>
            <ItemAgentBadge status={agentStatus.get(r.id)} />
          </span>
        ),
      },
      { id: 'organisation', label: t('tickets.fields.organisation'), groupable: true, value: (r) => r.organisationName ?? r.requesterEmail, cell: (r) => <span className="text-fg-2">{r.organisationName ?? r.requesterEmail ?? '–'}</span>, className: 'hidden md:table-cell' },
      { id: 'requester', label: t('tickets.fields.requester'), hidden: true, value: (r) => r.requesterName ?? r.requesterEmail, cell: (r) => r.requesterName ?? r.requesterEmail ?? '–' },
      {
        id: 'status',
        label: t('common.status'),
        type: 'enum',
        groupable: true,
        options: TICKET_STATUSES.map((x) => ({ value: x, label: t(`tickets.status.${x}`) })),
        value: (r) => r.status,
        cell: (r) => <TicketStatusBadge status={r.status} />,
      },
      {
        id: 'sla',
        label: t('sla.title'),
        type: 'enum',
        groupable: true,
        options: (['none', 'ok', 'at_risk', 'breached', 'paused', 'met'] as const).map((x) => ({ value: x, label: t(`sla.state.${x}`) })),
        value: (r) => r.sla.state,
        cell: (r) => <SlaBadge sla={r.sla} />,
      },
      {
        id: 'priority',
        label: t('tickets.fields.priority'),
        type: 'enum',
        groupable: true,
        options: TICKET_PRIORITIES.map((x) => ({ value: x, label: t(`tickets.priority.${x}`) })),
        value: (r) => r.priority,
        cell: (r) => <TicketPriorityBadge priority={r.priority} />,
      },
      { id: 'type', label: t('tickets.fields.type'), type: 'enum', hidden: true, options: TICKET_TYPES.map((x) => ({ value: x, label: t(`tickets.type.${x}`) })), value: (r) => r.type, cell: (r) => t(`tickets.type.${r.type}`) },
      { id: 'assignee', label: t('tickets.fields.assignee'), groupable: true, value: (r) => r.assigneeName, cell: (r) => <span className="text-fg-2">{r.assigneeName ?? '–'}</span>, className: 'hidden sm:table-cell' },
      { id: 'category', label: t('tickets.fields.category'), hidden: true, groupable: true, value: (r) => r.category, cell: (r) => r.category ?? '–' },
      { id: 'updated', label: t('tickets.fields.updated'), type: 'date', value: (r) => r.updatedAt, cell: (r) => <span className="text-fg-3">{formatDateTime(r.updatedAt)}</span>, className: 'hidden lg:table-cell' },
    ],
    [t, agentStatus],
  );

  return (
    <>
      <PageHeader
        title={t('tickets.title')}
        description={t('tickets.description')}
        actions={
          canCreate && (
            <Button onClick={() => setAdding(true)}>
              <Plus /> {t('tickets.new')}
            </Button>
          )
        }
      />
      <SupportOverview />
      <div className="mb-3 flex flex-wrap gap-1" role="group" aria-label={t('tickets.views.title')}>
        <Button size="sm" variant={!Object.values(s).some((x) => x !== undefined) ? 'secondary' : 'ghost'} aria-pressed={!Object.values(s).some((x) => x !== undefined)} onClick={() => void navigate({ search: {} as Search, replace: true })}>
          {t('tickets.views.open')}
        </Button>
        {VIEWS.map((v) => (
          <Button key={v.key} size="sm" variant={view === v.key ? 'secondary' : 'ghost'} aria-pressed={view === v.key} onClick={() => void navigate({ search: v.search as Search, replace: true })}>
            {t(`tickets.views.${v.key}`)}
          </Button>
        ))}
      </div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('common.search')} className="w-64">
          {(p) => <Input {...p} value={s.q ?? ''} placeholder={t('tickets.searchHint')} onChange={(e) => set({ q: e.target.value || undefined })} />}
        </Field>
        <Field label={t('common.status')} className="w-44">
          {(p) => (
            <NativeSelect {...p} value={s.status ?? ''} onChange={(e) => set({ status: (e.target.value || undefined) as never })}>
              <option value="">{t('tickets.openOnes')}</option>
              {TICKET_STATUSES.map((v) => (
                <option key={v} value={v}>
                  {t(`tickets.status.${v}`)}
                </option>
              ))}
              <option value="all">{t('common.all')}</option>
            </NativeSelect>
          )}
        </Field>
        <Field label={t('tickets.fields.priority')} className="w-40">
          {(p) => (
            <NativeSelect {...p} value={s.priority ?? ''} onChange={(e) => set({ priority: (e.target.value || undefined) as never })}>
              <option value="">{t('common.all')}</option>
              {TICKET_PRIORITIES.map((v) => (
                <option key={v} value={v}>
                  {t(`tickets.priority.${v}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('sla.title')} className="w-48">
          {(p) => (
            <NativeSelect {...p} value={s.sla ?? ''} onChange={(e) => set({ sla: (e.target.value || undefined) as never })}>
              <option value="">{t('common.all')}</option>
              <option value="at_risk">{t('sla.filterAtRisk')}</option>
              <option value="breached">{t('sla.state.breached')}</option>
              <option value="paused">{t('sla.state.paused')}</option>
            </NativeSelect>
          )}
        </Field>
        <Field label={t('tickets.fields.organisation')} className="w-52">
          {(p) => (
            <NativeSelect {...p} value={s.organisationId ?? ''} onChange={(e) => set({ organisationId: e.target.value || undefined, engagementId: undefined })}>
              <option value="">{t('common.all')}</option>
              {(orgs.data?.pages.flatMap((pg) => pg.items) ?? []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        {s.organisationId && (
          <Field label={t('tickets.fields.engagement')} className="w-52">
            {(p) => (
              <NativeSelect {...p} value={s.engagementId ?? ''} onChange={(e) => set({ engagementId: e.target.value || undefined })}>
                <option value="">{t('common.all')}</option>
                {(engagements.data?.pages.flatMap((pg) => pg.items) ?? []).map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
        )}
        <Field label={t('tickets.sortBy')} className="w-48">
          {(p) => (
            <NativeSelect {...p} value={s.sort ?? 'sla'} onChange={(e) => set({ sort: e.target.value === 'sla' ? undefined : (e.target.value as Search['sort']) })}>
              {(['sla', 'updated', 'created', 'priority', 'number'] as const).map((v) => (
                <option key={v} value={v}>
                  {t(`tickets.sort.${v}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <Checkbox checked={!!s.mine} onCheckedChange={(v) => set({ mine: v === true || undefined, unassigned: undefined })} /> {t('tickets.mine')}
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <Checkbox checked={!!s.unassigned} onCheckedChange={(v) => set({ unassigned: v === true || undefined, mine: undefined })} /> {t('tickets.unassigned')}
        </label>
      </div>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !rows.length ? (
        <EmptyState title={t('tickets.empty')} description={canCreate ? t('tickets.emptyHint') : undefined} />
      ) : (
        <DataTable listKey="support.ticket.list" columns={columns} rows={rows} rowKey={(r) => r.id} onOpen={(r) => void navigate({ to: '/support/$id', params: { id: r.id } })} partial={q.hasNextPage} onNeedAll={() => q.hasNextPage && !q.isFetchingNextPage && void q.fetchNextPage()} />
      )}
      {q.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
      {adding && <NewTicketDialog onClose={() => setAdding(false)} />}
    </>
  );
}
